import React from 'react';
import PropTypes from 'prop-types';
import { PieChart, pieArcLabelClasses } from '@mui/x-charts/PieChart';
import { useDrawingArea } from '@mui/x-charts/hooks';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import { useTheme } from '@mui/material/styles';



const StyledText = styled('text', {
    shouldForwardProp: (prop) => prop !== 'variant',
})(({ theme }) => ({
    textAnchor: 'middle',
    dominantBaseline: 'central',
    fill: (theme.vars || theme).palette.text.primary,
    filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.2))',
    variants: [
        {
            props: { variant: 'primary' },
            style: {
                fontSize: '1.5rem',
                fontWeight: 700,
            },
        },
        {
            props: ({ variant }) => variant !== 'primary',
            style: {
                fontSize: '0.875rem',
                fontWeight: 500,
                opacity: 0.8,
            },
        },
    ],
}));

function PieCenterLabel({ primaryText, secondaryText }) {
    const { width, height, left, top } = useDrawingArea();
    const primaryY = top + height / 2 - 10;
    const secondaryY = primaryY + 24;

    return (
        <React.Fragment>
            <StyledText variant="primary" x={left + width / 2} y={primaryY}>
                {primaryText}
            </StyledText>
            <StyledText variant="secondary" x={left + width / 2} y={secondaryY}>
                {secondaryText}
            </StyledText>
        </React.Fragment>
    );
}

PieCenterLabel.propTypes = {
    primaryText: PropTypes.string.isRequired,
    secondaryText: PropTypes.string.isRequired,
};

const colors = [
    'hsl(195, 85%, 45%)',  // Bright Azure Blue
    'hsl(225, 70%, 50%)',  // Royal Blue
    'hsl(260, 65%, 55%)',  // Deep Purple
    'hsl(185, 75%, 45%)',  // Turquoise
];

export default function ChartUserByCountry({ chartData, loading }) {
    const theme = useTheme();
    const locationData = React.useMemo(() => chartData || [], [chartData]);
    const hasData = locationData.length > 0;

    // Take only top 4 locations, combine the rest into "Others"
    const processedData = React.useMemo(() => {
        if (!hasData) return [];

        const top4 = locationData.slice(0, 4);
        const othersSum = locationData.slice(4).reduce((sum, item) => sum + item.count, 0);

        const result = top4.map(item => ({
            id: item.location,
            value: item.count,
            label: item.location
        }));

        if (othersSum > 0) {

            result.push({
                id: 'Others',
                value: othersSum,
                label: 'Others'
            });
        }

        return result;
    }, [hasData, locationData]);

    const total = locationData.reduce((sum, item) => sum + item.count, 0);

    const getArcLabel = (params) => {
        if (!total) {
            return '0%';
        }
        const percent = params.value / total;
        return `${(percent * 100).toFixed(0)}%`;
    };



    return (
        <Card variant="outlined" sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            flexGrow: 1,
            background: 'linear-gradient(to bottom right, rgba(255,255,255,0.05), rgba(255,255,255,0))',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            borderRadius: '16px',
        }}>
            <CardContent>
                <Typography
                    component="h2"
                    variant="subtitle1"
                    sx={{
                        mb: 2,
                        fontWeight: 600,
                        textAlign: 'center',
                        fontSize: '1.1rem',
                    }}
                >
                    Top Locations Distribution
                </Typography>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',

                }}>
                    {hasData ? (
                        <PieChart
                            colors={colors}
                            series={[
                                {
                                    data: processedData,
                                    outerRadius: 140,  // Increased size
                                    innerRadius: 100,
                                    paddingAngle: 2,  // Increased for better proportion
                                    arcLabel: getArcLabel,
                                    highlightScope: { faded: 'global', highlighted: 'item' },
                                    faded: { innerRadius: 75, additionalRadius: -20, color: 'gray' },
                                },
                            ]}
                            height={300}  // Increased height
                            width={300}   // Increased width
                            margin={{ top: 0, bottom: 0, left: 10, right: 0 }}
                            slotProps={{
                                legend: { hidden: true },
                            }}
                            sx={{
                                [`& .${pieArcLabelClasses.root}`]: {
                                    fill: 'white',
                                    fontSize: '0.875rem',
                                    fontWeight: '600',
                                    filter: 'drop-shadow(0px 1px 1px rgba(0,0,0,0.5))',  // Text shadow effect
                                },
                            }}
                        >
                            <PieCenterLabel
                                primaryText={total.toLocaleString()}
                                secondaryText="Total Vehicles"
                            />
                        </PieChart>
                    ) : (
                        <Skeleton
                            variant="circular"
                            width={300}
                            height={300}
                            animation="wave"
                            sx={{
                                bgcolor: theme.palette.background.paper,
                                opacity: 0.7
                            }}
                        />
                    )}
                </Box>
            </CardContent>
        </Card>
    );
}
